import { audioContextClass } from './audioContext';
import { PCM_WORKLET_NAME } from './pcmFormat';
import workletUrl from './pcmDownsampler.worklet?worker&url';

/**
 * Captures a microphone stream as 16 kHz 16-bit mono PCM frames, the input
 * live transcription expects. Downsampling runs on the audio thread in a
 * worklet; the main thread only receives finished frames.
 */

export interface PcmCapture {
  stop: () => void;
}

const errNoWorkletSupport =
  'Live captions need Web Audio worklets, which this browser lacks.';
const errContextStalled = 'The audio context did not start in time.';

/** How long to give the context to start before giving up. */
const CONTEXT_START_TIMEOUT_MS = 3000;

// Contexts made outside a user gesture start suspended on Safari, and resume
// can hang there rather than fail, so it is given a deadline.
const startContext = async (context: AudioContext) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(errContextStalled)),
      CONTEXT_START_TIMEOUT_MS,
    );
  });
  try {
    await Promise.race([context.resume(), deadline]);
  } finally {
    clearTimeout(timer);
  }
  if (context.state !== 'running') {
    throw new Error(`The audio context is ${context.state}.`);
  }
};

/** Feeds `onFrame` 100 ms of 16 kHz PCM at a time until stopped. */
export const startPcmCapture = async (
  stream: MediaStream,
  onFrame: (frame: ArrayBuffer) => void,
): Promise<PcmCapture> => {
  const Context = audioContextClass();
  if (!Context || typeof AudioWorkletNode === 'undefined') {
    throw new Error(errNoWorkletSupport);
  }

  const context = new Context();
  try {
    await context.audioWorklet.addModule(workletUrl);
    await startContext(context);
  } catch (err) {
    // Best effort: the context may already be closed.
    context.close().catch(() => undefined);
    throw err;
  }

  const node = new AudioWorkletNode(context, PCM_WORKLET_NAME, {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    channelCount: 1,
    channelCountMode: 'explicit',
  });
  node.port.onmessage = (event: MessageEvent<ArrayBuffer>) =>
    onFrame(event.data);

  context.createMediaStreamSource(stream).connect(node);
  // The worklet writes no output, so this is silent; it only keeps the graph
  // running on browsers that idle nodes that lead nowhere.
  node.connect(context.destination);

  return {
    stop: () => {
      node.port.onmessage = null;
      node.disconnect();
      // Best effort: the context may already be closed.
      context.close().catch(() => undefined);
    },
  };
};
